// frontend/src/pages/CategoriasPage/CategoriasPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Typography, Button, Row, Col, Card, List, Modal, Spin, Alert, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { getCategorias, deleteCategoria } from '../../services/categoryService'; 
import CategoriaModal from '../../components/CategoriaModal/CategoriaModal';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { getErrorMessage } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import { markCategoriasPrimeirosPassosVisto } from '../../utils/primeirosPassosStorage';

const { Content } = Layout;
const { Title } = Typography;

const CategoriasPage = () => {
    const { message } = App.useApp();
    const { user } = useAuth();
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [categoriaEmEdicao, setCategoriaEmEdicao] = useState(null);

    // Estado simples para responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        markCategoriasPrimeirosPassosVisto(user?.id ?? user?.pk ?? 'anon');
    }, [user]);

    const [modal, modalContextHolder] = Modal.useModal();

    const fetchCategorias = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getCategorias();
            setCategorias(data);
            setError(null);
        } catch (err) {
            setError(getErrorMessage(err, 'Falha ao buscar categorias.'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategorias();
    }, [fetchCategorias]);

    const handleAbrirModalCriar = () => {
        setCategoriaEmEdicao(null);
        setIsModalOpen(true);
    };

    const handleAbrirModalEditar = (categoria) => {
        setCategoriaEmEdicao(categoria);
        setIsModalOpen(true);
    };

    const handleFecharModal = () => {
        setIsModalOpen(false);
        setCategoriaEmEdicao(null);
    };

    const handleSalvarSucesso = (msg) => {
        handleFecharModal();
        fetchCategorias();
        message.success(msg || 'Salvo com sucesso.');
    };
    
    const handleDeletar = (id) => {
        modal.confirm({
            title: 'Confirmar exclusão',
            icon: <ExclamationCircleFilled />,
            content: 'Tem certeza que deseja excluir esta categoria? Esta ação não pode ser desfeita.',
            okText: 'Excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteCategoria(id);
                    message.success('Categoria excluída com sucesso.');
                    fetchCategorias();
                } catch (err) {
                    message.error(getErrorMessage(err, 'Erro ao excluir categoria.'));
                }
            },
        });
    };

    if (loading && !isModalOpen) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '80vh' }}>
                <Spin tip="Carregando..." size="large" />
            </div>
        );
    }

    return (
        <Content style={{ 
            padding: isMobile ? '1rem' : '2rem', // Padding reduzido no mobile
            width: '100%' 
        }}>
            {modalContextHolder}

            {/* Cabeçalho responsivo */}
            <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', // Empilha no mobile
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                marginBottom: '2rem',
                gap: isMobile ? '1rem' : 0
            }}>
                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Gerenciar Categorias</Title>
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAbrirModalCriar}
                    block={isMobile} // Botão largura total no mobile
                    size={isMobile ? 'large' : 'middle'} // Botão maior no mobile
                >
                    Nova Categoria
                </Button>
            </div>

            {error && <Alert message="Erro" description={error} type="error" showIcon style={{ marginBottom: '1rem' }} />}

            <Row gutter={[isMobile ? 16 : 24, isMobile ? 16 : 24]}>
                <Col xs={24} md={12}>
                    <Card 
                        title="Categorias de Receita" 
                        headStyle={{backgroundColor: 'var(--color-surface-muted, #F8F4EC)'}}
                        bodyStyle={{ padding: isMobile ? '0' : '24px' }} // Remove padding do corpo no mobile para a lista encostar nas bordas
                        bordered={!isMobile} // Remove borda externa no mobile para visual clean
                    >
                        <List
                            dataSource={categorias.filter(c => c.tipo === 'receita')}
                            locale={{
                                emptyText: (
                                    <EmptyStateGuide
                                        title="Nenhuma categoria de receita."
                                        description="Organize suas entradas criando categorias de receita."
                                        actionLabel="Nova categoria"
                                        onAction={handleAbrirModalCriar}
                                    />
                                ),
                            }}
                            renderItem={(cat) => (
                                <List.Item 
                                    style={{ padding: isMobile ? '12px 16px' : undefined }} // Padding interno controlado
                                    actions={[
                                        <Button icon={<EditOutlined />} type="text" onClick={() => handleAbrirModalEditar(cat)} size="large" />, // Ícones maiores
                                        <Button icon={<DeleteOutlined />} type="text" danger onClick={() => handleDeletar(cat.id)} size="large" />
                                    ]}
                                >
                                    <List.Item.Meta 
                                        title={<span style={{ fontSize: isMobile ? '16px' : '14px' }}>{cat.nome}</span>} 
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card 
                        title="Categorias de Despesa" 
                        headStyle={{backgroundColor: 'var(--color-surface-muted, #F8F4EC)'}}
                        bodyStyle={{ padding: isMobile ? '0' : '24px' }}
                        bordered={!isMobile}
                    >
                        <List
                            dataSource={categorias.filter(c => c.tipo === 'despesa')}
                            locale={{
                                emptyText: (
                                    <EmptyStateGuide
                                        title="Nenhuma categoria de despesa."
                                        description="Organize seus gastos criando categorias de despesa."
                                        actionLabel="Nova categoria"
                                        onAction={handleAbrirModalCriar}
                                    />
                                ),
                            }}
                            renderItem={(cat) => (
                                <List.Item 
                                    style={{ padding: isMobile ? '12px 16px' : undefined }}
                                    actions={[
                                        <Button icon={<EditOutlined />} type="text" onClick={() => handleAbrirModalEditar(cat)} size="large" />,
                                        <Button icon={<DeleteOutlined />} type="text" danger onClick={() => handleDeletar(cat.id)} size="large" />
                                    ]}
                                >
                                    <List.Item.Meta 
                                        title={<span style={{ fontSize: isMobile ? '16px' : '14px' }}>{cat.nome}</span>} 
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>
            
            <CategoriaModal 
                isOpen={isModalOpen}
                onClose={handleFecharModal} 
                onSave={handleSalvarSucesso}
                categoria={categoriaEmEdicao}
            />
        </Content>
    );
};

export default CategoriasPage;